import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  MicPermissionDeniedError,
  recordWithSilenceDetection,
} from "@/lib/audioRecorder";
import { playPrompt, stopAllAudio } from "@/lib/gateAudio";
import { formatDobDigits } from "@/lib/dateInput";
import { RecordingPulse, SpeakerPulse } from "@/components/interview/_visuals";

// GOVERNING RULE (whole file): for the interview gates the manually tapped/typed
// value is AUTHORITATIVE; the microphone recording + STT are a SUPPORTING artifact
// only. A subject is NEVER blocked because the consent audio is silent, weak,
// noisy, or unparseable — provided the typed/tapped value is valid. The manual
// entry control is therefore present from the start on every gate, not gated
// behind a voice-recognition miss. Background audio capture is preserved (a
// manual commit aborts the recorder to keep whatever audio exists, best-effort).

const MAX_RETRIES = 3;
// Gate answer window (shared by consent/state/dob). Shortened from 15_000/3_500
// so a short spoken answer ("yes") auto-stops promptly instead of leaving the mic
// open long enough to capture background words. TUNING VALUES — do NOT
// over-tighten; a slow/quiet elderly "yes" must still register. Needs real-device
// testing alongside the audioRecorder AGC-off change.
const ANSWER_MAX_DURATION_MS = 8_000;
const GATE_SILENCE_MS = 2_500;

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Client-side US states + DC for the manual state autocomplete ONLY (UX). The
// server re-validates the submitted code against the authoritative counsel-owned
// list in supabase/functions/_shared/eligibility.ts — this list is display sugar,
// never the source of truth.
const US_STATES: ReadonlyArray<{ code: string; name: string }> = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" }, { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" },
  { code: "DC", name: "District of Columbia" }, { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" }, { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" }, { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" }, { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" }, { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" }, { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" }, { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" }, { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
];

// Resolve free-typed text to a canonical {code, name}, accepting either a full
// state name or a 2-letter code (case-insensitive). Returns null if unrecognized.
const resolveUsState = (raw: string): { code: string; name: string } | null => {
  const t = raw.trim().toLowerCase();
  if (!t) return null;
  const byName = US_STATES.find((s) => s.name.toLowerCase() === t);
  if (byName) return byName;
  const byCode = US_STATES.find((s) => s.code.toLowerCase() === t);
  return byCode ?? null;
};

// Parse a typed MM/DD/YYYY into integer y/m/d, validating it is a real calendar
// date. Built DIRECTLY (not round-tripped through the server's speech parseDob).
const parseTypedDob = (
  raw: string,
): { year: number; month: number; day: number } | null => {
  const m = raw.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  const month = parseInt(m[1], 10);
  const day = parseInt(m[2], 10);
  const year = parseInt(m[3], 10);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  if (year < 1900 || year > new Date().getFullYear()) return null;
  const d = new Date(year, month - 1, day);
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) {
    return null;
  }
  return { year, month, day };
};

const capitalizeWords = (s: string) =>
  s.replace(/\b\w/g, (c) => c.toUpperCase());

export type Gate = "consent" | "state" | "dob";

type GatePromptConfig = {
  prompt: string;
  mp3Url?: string;
};

const GATE_CONFIG: Record<Gate, GatePromptConfig> = {
  consent: {
    prompt:
      "I'm going to ask you 23 questions about your life. I'll record your answers in your voice. We'll use those recordings to create an AI version of your voice that can answer questions from your family and visitors to your tribute in the future. The recordings and your AI voice will be kept as part of your tribute. You can ask us to delete everything at any time. Do you understand and consent to this? Please say yes or no.",
    mp3Url: "/audio/interview/v1/gate-consent.mp3",
  },
  state: {
    prompt: "What U.S. state do you live in? Please say the full name of the state.",
    mp3Url: "/audio/interview/v1/gate-state.mp3",
  },
  dob: {
    prompt: "What is your date of birth? Please say the month, day, and year.",
    mp3Url: "/audio/interview/v1/gate-dob.mp3",
  },
};

type ParsedConsent = { value: "yes" | "no" | "unclear" };
type ParsedState = { state_code: string | null; state_name: string | null; eligible: boolean };
type ParsedDob = {
  year: number | null;
  month: number | null;
  day: number | null;
  age: number | null;
  eligible: boolean;
};

type GateSttResponse = {
  ok: true;
  gate: string;
  transcript: string;
  recognized: boolean;
  parsed: ParsedConsent | ParsedState | ParsedDob;
  outcome: "accept" | "retry" | "terminate";
  audio_key: string | null;
  // Set by the server when voice/STT named an EXCLUDED state. Rides alongside
  // outcome:"retry" — the subject must re-confirm via the authoritative typed field
  // rather than by voice, because STT is inference and terminating on it is
  // irreversible. Optional so an older server response is simply falsy here.
  requires_manual_confirmation?: boolean;
  terminated: boolean;
  terminated_status: "terminated_declined" | "terminated_ineligible" | null;
  terminated_reason:
    | "declined_at_consent_gate"
    | "ineligible_geography"
    | "ineligible_age"
    | "spoken_state_excluded"
    | null;
};

export type TerminateInfo = {
  status: "terminated_declined" | "terminated_ineligible";
  reason:
    | "declined_at_consent_gate"
    | "ineligible_geography"
    | "ineligible_age"
    | "spoken_state_excluded";
};

export type ConsentGateProps = {
  gate: Gate;
  resumeToken: string;
  audioContext: AudioContext;
  // Optional gate-phase mic stream acquired once on Begin (InterviewLanding) and
  // reused across the three gates so iOS doesn't re-prompt per gate. When absent
  // (acquisition denied/failed), each recording acquires its own stream as before.
  micStream?: MediaStream;
  onAccept: (parsed: ParsedConsent | ParsedState | ParsedDob) => void;
  onTerminate: (info: TerminateInfo) => void;
  onMaxRetries: (gate: Gate) => void;
  onMicDenied: (gate: Gate) => void;
  onError: (message: string) => void;
};

// The AUTHORITATIVE manual value the subject taps/types. Structured per gate so
// the server computes the result from it directly — no STT, no transcript.
type ManualValue =
  | { kind: "consent"; outcome: "yes" | "no" }
  | { kind: "state"; state_code: string; state_name: string }
  | { kind: "dob"; year: number; month: number; day: number };

type Phase =
  | "playing_prompt"
  | "recording"
  | "uploading"
  | "reviewing"
  // Voice named an excluded state: the loop is parked on the manual-entry panel
  // awaiting an authoritative typed re-confirmation. Deliberately NOT "uploading",
  // whose visual is a spinner — the subject must be told to type, not watched spin.
  | "awaiting_manual_confirm";

const formatParsedValue = (gate: Gate, parsed: GateSttResponse["parsed"]): string => {
  if (gate === "consent") return "Yes";
  if (gate === "state") {
    const p = parsed as ParsedState;
    return p.state_name ? capitalizeWords(p.state_name) : "your state";
  }
  const p = parsed as ParsedDob;
  if (p.month && p.day && p.year) {
    return `${MONTH_NAMES[p.month - 1]} ${p.day}, ${p.year}`;
  }
  if (p.year) return String(p.year);
  return "your date of birth";
};

// Calls the gate endpoint. `manual` (when present) is the authoritative value and
// makes the server SKIP STT entirely; `blob` is the supporting artifact and is
// OPTIONAL — a null/empty blob is fine and does not block the manual result.
const callGateStt = async (
  resumeToken: string,
  gate: Gate,
  blob: Blob | null,
  mimeType: string,
  manual?: ManualValue,
): Promise<{ data?: GateSttResponse; status?: number; error?: string }> => {
  const form = new FormData();
  form.append("gate", gate);
  if (blob && blob.size > 0) {
    const ext = mimeType.includes("mp4") || mimeType.includes("aac") ? "m4a" : "webm";
    form.append("audio", new File([blob], `${gate}.${ext}`, { type: mimeType }));
  }
  // Manual channels — one per gate. The server rejects cross-gate mismatches and,
  // when a manual value is present, computes the result from it and skips STT.
  if (manual) {
    if (manual.kind === "consent") {
      form.append("manual_outcome", manual.outcome);
    } else if (manual.kind === "state") {
      form.append("manual_state", manual.state_code);
      form.append("manual_state_name", manual.state_name);
    } else {
      const pad = (n: number) => String(n).padStart(2, "0");
      form.append(
        "manual_dob",
        `${manual.year}-${pad(manual.month)}-${pad(manual.day)}`,
      );
    }
  }

  const { data, error } = await supabase.functions.invoke<GateSttResponse>(
    "interview-consent-gate-stt",
    {
      body: form,
      headers: { Authorization: `Bearer ${resumeToken}` },
    },
  );

  if (error) {
    const ctx = (error as unknown as { context?: Response }).context;
    let status: number | undefined;
    let message = "request failed";
    if (ctx && typeof ctx.status === "number") {
      status = ctx.status;
      try {
        const body = (await ctx.clone().json()) as { error?: string };
        message = body?.error ?? message;
      } catch {
        // leave default
      }
    }
    return { status, error: message };
  }
  if (!data?.ok) return { error: "malformed response" };
  return { data };
};

const ConsentGate = ({
  gate,
  resumeToken,
  audioContext,
  micStream,
  onAccept,
  onTerminate,
  onMaxRetries,
  onMicDenied,
  onError,
}: ConsentGateProps) => {
  const [phase, setPhase] = useState<Phase>("playing_prompt");
  const [attempt, setAttempt] = useState(1);
  const [reviewParsed, setReviewParsed] = useState<GateSttResponse["parsed"] | null>(null);
  // On-screen retry cue ("Let's try that again." / "This is your last try...")
  // shown during playing_prompt/recording. Surfaced VISUALLY so the retry replays
  // the narrator clip instead of speaking the preamble in the robotic voice.
  const [retryPreamble, setRetryPreamble] = useState<string>("");
  // Local inputs for the always-visible manual entry panel (state / dob).
  const [manualStateText, setManualStateText] = useState("");
  const [manualDobText, setManualDobText] = useState("");
  const [manualError, setManualError] = useState<string>("");

  // Insulate the loop from parent re-renders. Callback identities change on
  // every render but the loop must not restart, so we stash them in a ref.
  const cbRef = useRef({ onAccept, onTerminate, onMaxRetries, onMicDenied, onError });
  cbRef.current = { onAccept, onTerminate, onMaxRetries, onMicDenied, onError };

  // Manual Done terminator for the gate recording. Silence detection stays the
  // primary terminator; Done is the backup. Mirrors QuestionFlow's answerStopRef.
  const gateStopRef = useRef<AbortController | null>(null);
  // Single-shot resolver for the reviewing tap-confirm (Yes / Re-record). Nulled
  // before calling so a double-tap can't resolve twice.
  const gateConfirmResolveRef = useRef<((d: "yes" | "re-record") => void) | null>(null);
  // Single-shot resolver for the ALWAYS-visible manual entry panel. Set at the
  // start of each iteration; a commit resolves it with the authoritative value,
  // short-circuiting the voice path wherever the loop is currently awaiting.
  const manualResolveRef = useRef<((v: ManualValue) => void) | null>(null);
  // Set true once the subject starts typing/focusing a manual field. It SUSPENDS
  // the voice record+retry cycle for this gate so the 8s max-duration can't fire
  // (and re-ask / re-arm the mic) while the subject is mid-entry. Reset at the
  // start of each iteration.
  const manualEntryActiveRef = useRef(false);

  // The subject started manual entry (focus or first keystroke on a manual field).
  // Immediately end the live recording window and silence the narrator so nothing
  // talks over / re-asks the typing subject; the loop then parks on the manual
  // commit instead of uploading silence and re-cycling. Idempotent.
  const beginManualEntry = () => {
    if (manualEntryActiveRef.current) return;
    manualEntryActiveRef.current = true;
    gateStopRef.current?.abort();
    stopAllAudio();
  };

  // Commit an authoritative manual value (from the panel). Nulls the resolver
  // first so a double-tap cannot resolve twice; a no-op if we are not currently
  // awaiting an answer (e.g. mid-upload), in which case the panel is hidden.
  const commitManual = (v: ManualValue) => {
    const resolve = manualResolveRef.current;
    if (!resolve) return;
    manualResolveRef.current = null;
    setManualError("");
    resolve(v);
  };

  const commitStateManual = () => {
    const match = resolveUsState(manualStateText);
    if (!match) {
      setManualError("Please choose your state from the list.");
      return;
    }
    commitManual({ kind: "state", state_code: match.code, state_name: match.name });
  };

  const commitDobManual = () => {
    const d = parseTypedDob(manualDobText);
    if (!d) {
      setManualError("Please enter your date of birth as MM/DD/YYYY.");
      return;
    }
    commitManual({ kind: "dob", year: d.year, month: d.month, day: d.day });
  };

  useEffect(() => {
    let alive = true;
    const config = GATE_CONFIG[gate];

    // Resolves when the subject taps Yes or Re-record on the reviewing screen.
    function waitForGateConfirm(): Promise<"yes" | "re-record"> {
      return new Promise((resolve) => {
        gateConfirmResolveRef.current = resolve;
      });
    }

    // Submit an AUTHORITATIVE manual value. Audio is best-effort (may be null).
    // The server skips STT and computes accept/terminate from the structured
    // value; a silent/weak/absent recording never blocks this path.
    const submitManual = async (
      v: ManualValue,
      blob: Blob | null,
      mimeType: string,
    ): Promise<void> => {
      // Stop any prompt playback and the recorder (best-effort audio capture).
      stopAllAudio();
      gateStopRef.current?.abort();
      setPhase("uploading");
      const result = await callGateStt(resumeToken, gate, blob, mimeType, v);
      if (!alive) return;
      if (result.error || !result.data) {
        if (result.status === 401 || result.status === 403) {
          cbRef.current.onError("Your session has expired. Please reopen the email link.");
          return;
        }
        cbRef.current.onError(result.error ?? "Couldn't save your answer. Please try again.");
        return;
      }
      const r = result.data;
      if (r.outcome === "terminate" && r.terminated_status && r.terminated_reason) {
        cbRef.current.onTerminate({
          status: r.terminated_status,
          reason: r.terminated_reason,
        });
        return;
      }
      if (r.outcome === "accept") {
        cbRef.current.onAccept(r.parsed);
        return;
      }
      // A manual value is authoritative and should never yield "retry"; treat an
      // unexpected non-accept as a soft error rather than silently stalling.
      cbRef.current.onError("Couldn't save your answer. Please try again.");
    };

    const runIteration = async (
      currentAttempt: number,
      replayClean = false,
    ): Promise<void> => {
      setAttempt(currentAttempt);
      setReviewParsed(null);
      // Each iteration starts a fresh voice attempt; manual-entry suspend is
      // per-iteration and re-armed only when the subject touches a manual field.
      manualEntryActiveRef.current = false;

      // Fresh manual-commit promise for THIS iteration. The always-visible panel
      // resolves it; `manualValue` is also captured so post-await checks (after
      // the recorder/STT) can detect a commit that raced the voice path.
      let manualValue: ManualValue | null = null;
      const manualCommitted = new Promise<ManualValue>((resolve) => {
        manualResolveRef.current = (v) => {
          manualValue = v;
          resolve(v);
        };
      });

      try {
        // ---- 1. Play the gate prompt (race the manual panel) ----------------
        // replayClean = a user-initiated Re-record: replay the clean gate mp3
        // with no "let's try again" preamble. Retries ALWAYS replay the clean
        // narrator gate clip (cached from attempt 1). The retry cue is surfaced
        // ON-SCREEN via retryPreamble; never spoken in the robotic voice.
        const isLastTry = currentAttempt === MAX_RETRIES;
        setRetryPreamble(
          replayClean
            ? ""
            : isLastTry
            ? "This is your last try for this question."
            : currentAttempt > 1
            ? "Let's try that again."
            : "",
        );
        setPhase("playing_prompt");
        const played = playPrompt(config.prompt, config.mp3Url).then(
          () => "played" as const,
        );
        // Manual commit during the prompt wins immediately (no audio yet).
        const step1 = await Promise.race([
          played,
          manualCommitted.then((v) => ({ manual: v })),
        ]);
        if (!alive) return;
        if (typeof step1 !== "string") {
          await submitManual(step1.manual, null, "");
          return;
        }

        // ---- 2. Record the answer (race the manual panel) -------------------
        setPhase("recording");
        const gateStop = new AbortController();
        gateStopRef.current = gateStop;
        // Manual entry may have begun during the prompt (beginManualEntry ran when
        // gateStopRef was still null, so its abort was a no-op). End this recording
        // immediately rather than holding the mic open for the full window while
        // the subject types; the post-record suspend arm then parks on the commit.
        if (manualEntryActiveRef.current) gateStop.abort();
        // A manual commit during recording aborts the recorder so we still keep
        // whatever audio was captured (supporting artifact), then submit below.
        void manualCommitted.then(() => {
          try {
            gateStop.abort();
          } catch {
            // recorder already finalized
          }
        });
        const answer = await recordWithSilenceDetection({
          maxDurationMs: ANSWER_MAX_DURATION_MS,
          silenceMs: GATE_SILENCE_MS,
          audioContext,
          stopSignal: gateStop.signal,
          // Gates need AGC-ON so a quiet/normal elderly voice is boosted enough to
          // clear the RMS silence floor and to transcribe — autoGainControl:false
          // was the regression that made state/DOB barely recognize normal speech.
          // Keep noise/echo rejection for the short yes/no window. Scoped here so
          // voice-clone training capture (QuestionFlow/FinalConsentRecording) keeps
          // the bare default and is unaffected.
          audioConstraints: {
            noiseSuppression: true,
            echoCancellation: true,
            autoGainControl: true,
          },
          // Don't let the trailing-silence timer finalize on a quiet lead-in or a
          // brief mid-answer dip: require at least one above-threshold (speech)
          // frame first, and never stop before 800ms of recording has elapsed.
          requireSpeechBeforeSilence: true,
          minSpeechMs: 800,
          // Reuse the gate-phase stream acquired once on Begin (no per-gate iOS
          // re-prompt). When undefined, the recorder acquires its own stream and
          // the audioConstraints above apply as before. The recorder ignores
          // audioConstraints when a sharedStream is present.
          sharedStream: micStream,
        });
        gateStopRef.current = null;
        if (!alive) return;

        // Manual committed during recording — it is authoritative; submit it with
        // whatever audio the aborted recorder captured.
        if (manualValue) {
          await submitManual(manualValue, answer.blob, answer.mimeType);
          return;
        }

        // Manual entry is IN PROGRESS (subject started typing/focusing a field)
        // but not yet committed: beginManualEntry aborted the recorder to end the
        // 8s window. SUSPEND the voice path — do NOT upload silence or run
        // STT/retry (which would re-ask and re-arm the mic under the typing
        // subject). Park on the manual commit; submit it with the captured audio.
        if (manualEntryActiveRef.current) {
          const v = await manualCommitted;
          if (!alive) return;
          await submitManual(v, answer.blob, answer.mimeType);
          return;
        }

        // ---- 3. Voice path: upload to consent-gate-stt ----------------------
        setPhase("uploading");
        const result = await callGateStt(resumeToken, gate, answer.blob, answer.mimeType);
        if (!alive) return;

        // Subject committed manually while STT was in flight — manual wins.
        if (manualValue) {
          await submitManual(manualValue, answer.blob, answer.mimeType);
          return;
        }

        if (result.error || !result.data) {
          if (result.status === 401 || result.status === 403) {
            cbRef.current.onError("Your session has expired. Please reopen the email link.");
            return;
          }
          cbRef.current.onError(result.error ?? "Couldn't reach the server. Please try again.");
          return;
        }

        const r = result.data;
        // NOTE: r.transcript is intentionally NOT surfaced. The raw STT string is
        // untrustworthy (a moan can transcribe as foreign-script garbage) and is a
        // trust hazard; it is used server-side for logs only, never shown here.

        if (r.outcome === "terminate" && r.terminated_status && r.terminated_reason) {
          cbRef.current.onTerminate({
            status: r.terminated_status,
            reason: r.terminated_reason,
          });
          return;
        }

        // Voice named an EXCLUDED state. The server did not terminate on it (STT is
        // inference, and terminating on inference is irreversible) — it requires an
        // authoritative typed re-confirmation instead. Park on the manual commit and
        // do NOT re-ask by voice: replaying the prompt would just re-hear the same
        // state and burn retries down to gate_max_retries, a dead end for a subject
        // who may well be eligible. Checked BEFORE the retry branch, which this
        // response also carries (see the server comment on why it reuses "retry").
        // The subsequent submitManual runs the manual branch, which is authoritative:
        // an excluded confirmation terminates there, an eligible one proceeds.
        if (r.requires_manual_confirmation) {
          setPhase("awaiting_manual_confirm");
          const v = await manualCommitted;
          if (!alive) return;
          await submitManual(v, answer.blob, answer.mimeType);
          return;
        }

        if (r.outcome === "retry") {
          // If the subject began manual entry (e.g. focused a field while STT was
          // in flight), suspend rather than re-ask: park on the manual commit
          // instead of replaying the prompt and re-arming the mic under them.
          if (manualEntryActiveRef.current) {
            const v = await manualCommitted;
            if (!alive) return;
            await submitManual(v, answer.blob, answer.mimeType);
            return;
          }
          // Voice couldn't parse the answer. The manual panel has been visible the
          // whole time, so the subject was never stuck. Re-ask up to MAX_RETRIES;
          // after that, hand off to the parent (the manual escape was always
          // available during the attempts).
          if (currentAttempt >= MAX_RETRIES) {
            cbRef.current.onMaxRetries(gate);
            return;
          }
          await runIteration(currentAttempt + 1);
          return;
        }

        // ---- 4. Voice accept: confirm (or manual override) ------------------
        // Audio is already persisted at STT. The tap is UI-only: Yes advances;
        // Re-record replays the prompt and re-records. A manual commit here still
        // overrides — the manual value is authoritative.
        setReviewParsed(r.parsed);
        setPhase("reviewing");
        const decision = await Promise.race([
          waitForGateConfirm().then((d) => ({ confirm: d })),
          manualCommitted.then((v) => ({ manual: v })),
        ]);
        if (!alive) return;
        if ("manual" in decision) {
          await submitManual(decision.manual, answer.blob, answer.mimeType);
          return;
        }
        if (decision.confirm === "yes") {
          cbRef.current.onAccept(r.parsed);
          return;
        }
        // Re-record chosen. But if manual entry has since begun, do NOT replay the
        // prompt / re-arm the mic — park on the manual commit instead.
        if (manualEntryActiveRef.current) {
          const v = await manualCommitted;
          if (!alive) return;
          await submitManual(v, answer.blob, answer.mimeType);
          return;
        }
        // Re-record: re-run from the prompt with the clean mp3; does NOT count
        // against MAX_RETRIES (same attempt number).
        await runIteration(currentAttempt, true);
      } catch (err) {
        if (!alive) return;
        if (err instanceof MicPermissionDeniedError) {
          cbRef.current.onMicDenied(gate);
          return;
        }
        const msg = err instanceof Error ? err.message : "unknown error";
        cbRef.current.onError(`Recording failed: ${msg}`);
      }
    };

    void runIteration(1);

    return () => {
      alive = false;
      stopAllAudio();
      gateStopRef.current?.abort();
      manualResolveRef.current = null;
    };
  }, [gate, resumeToken, audioContext]);

  // The manual panel is available whenever we are awaiting an answer. Includes
  // "uploading" so the field does NOT vanish mid-typing if the 8s voice window
  // finalized right as the subject began entering their answer.
  const showManualPanel =
    phase === "playing_prompt" ||
    phase === "recording" ||
    phase === "reviewing" ||
    phase === "uploading" ||
    // The panel IS the only way forward in this phase — it must never be hidden.
    phase === "awaiting_manual_confirm";

  return (
    <div className="text-center" aria-live="polite">
      <div className="mb-6 text-sm uppercase tracking-wide text-muted-foreground">
        {gate === "consent" ? "Consent" : gate === "state" ? "State of residence" : "Date of birth"}
        {attempt > 1 ? ` · Try ${attempt} of ${MAX_RETRIES}` : ""}
      </div>

      <PhaseVisual phase={phase} />

      {retryPreamble && (phase === "playing_prompt" || phase === "recording") ? (
        <p className="mt-4 text-base font-medium text-foreground">{retryPreamble}</p>
      ) : null}

      {phase === "recording" ? (
        <div className="mt-6">
          <Button
            size="lg"
            onClick={() => gateStopRef.current?.abort()}
            className="text-xl px-10 py-7 h-auto rounded-2xl"
          >
            Done
          </Button>
        </div>
      ) : null}

      {phase === "reviewing" ? (
        <div className="mt-8 flex flex-col gap-4 items-stretch max-w-sm mx-auto">
          <div className="rounded-2xl border border-border px-6 py-5 text-center">
            <p className="text-sm text-muted-foreground">
              {gate === "consent" ? "You said:" : "We heard:"}
            </p>
            {/* Parsed value only — never the raw STT transcript. */}
            <p className="mt-1 text-xl font-semibold text-foreground">
              {reviewParsed ? formatParsedValue(gate, reviewParsed) : ""}
            </p>
          </div>
          <Button
            size="lg"
            onClick={() => {
              const resolve = gateConfirmResolveRef.current;
              gateConfirmResolveRef.current = null;
              resolve?.("yes");
            }}
            className="text-xl px-10 py-6 h-auto rounded-2xl"
          >
            ✓ Yes, that's right
          </Button>
          <Button
            size="lg"
            variant="outline"
            onClick={() => {
              const resolve = gateConfirmResolveRef.current;
              gateConfirmResolveRef.current = null;
              resolve?.("re-record");
            }}
            className="text-xl px-10 py-6 h-auto rounded-2xl"
          >
            ↻ Re-record
          </Button>
        </div>
      ) : null}

      {/* ALWAYS-VISIBLE manual entry (voice stays primary in tone; typing/tapping
          is the guaranteed path). Present from the start on every gate. */}
      {showManualPanel ? (
        <ManualEntryPanel
          gate={gate}
          manualStateText={manualStateText}
          manualDobText={manualDobText}
          manualError={manualError}
          onManualEntryStart={beginManualEntry}
          onStateText={(v) => {
            beginManualEntry();
            if (manualError) setManualError("");
            setManualStateText(v);
          }}
          onDobText={(v) => {
            beginManualEntry();
            if (manualError) setManualError("");
            setManualDobText(formatDobDigits(v));
          }}
          onConsentYes={() => commitManual({ kind: "consent", outcome: "yes" })}
          onConsentNo={() => commitManual({ kind: "consent", outcome: "no" })}
          onStateSubmit={commitStateManual}
          onDobSubmit={commitDobManual}
        />
      ) : null}
    </div>
  );
};

type ManualEntryPanelProps = {
  gate: Gate;
  manualStateText: string;
  manualDobText: string;
  manualError: string;
  // Called the instant the subject begins manual entry (focus or first keystroke
  // on a typed field) so the voice record/retry cycle is suspended for this gate.
  onManualEntryStart: () => void;
  onStateText: (v: string) => void;
  onDobText: (v: string) => void;
  onConsentYes: () => void;
  onConsentNo: () => void;
  onStateSubmit: () => void;
  onDobSubmit: () => void;
};

const ManualEntryPanel = ({
  gate,
  manualStateText,
  manualDobText,
  manualError,
  onManualEntryStart,
  onStateText,
  onDobText,
  onConsentYes,
  onConsentNo,
  onStateSubmit,
  onDobSubmit,
}: ManualEntryPanelProps) => {
  // Local, presentational-only confirmation for the manual "No" (decline ends the
  // interview and can't be undone). This state NEVER touches the manual resolver,
  // recorder, or parent — only the confirm button calls onConsentNo; cancel is a
  // pure no-op.
  const [confirmingNo, setConfirmingNo] = useState(false);

  return (
    <div className="mt-8 flex flex-col gap-3 items-stretch max-w-sm mx-auto">
      <p className="text-sm text-muted-foreground">
        {gate === "consent"
          ? "You can answer out loud, or tap your answer:"
          : "You can answer out loud, or enter it yourself:"}
      </p>

      {gate === "consent" ? (
        confirmingNo ? (
          // In-place confirmation: shown INSTEAD of the Yes/No buttons so an
          // accidental tap can't dismiss it. Cancel returns with no side effects.
          <div className="flex flex-col gap-3">
            <p className="text-base font-medium text-foreground">
              End the interview? This can't be undone.
            </p>
            <Button
              size="lg"
              variant="destructive"
              onClick={onConsentNo}
              className="text-lg px-8 py-6 h-auto rounded-2xl"
            >
              Yes, end it
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => setConfirmingNo(false)}
              className="text-lg px-8 py-6 h-auto rounded-2xl"
            >
              Go back
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <Button
              size="lg"
              onClick={onConsentYes}
              className="text-lg px-8 py-6 h-auto rounded-2xl"
            >
              ✓ Yes, I consent
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => setConfirmingNo(true)}
              className="text-lg px-8 py-6 h-auto rounded-2xl"
            >
              ✗ No
            </Button>
          </div>
        )
      ) : null}

      {gate === "state" ? (
        <div className="flex flex-col gap-3 text-left">
          <Label htmlFor="manual-state">U.S. state of residence</Label>
          <Input
            id="manual-state"
            list="manual-state-options"
            autoComplete="off"
            placeholder="Start typing your state"
            value={manualStateText}
            onFocus={onManualEntryStart}
            onChange={(e) => onStateText(e.target.value)}
          />
          <datalist id="manual-state-options">
            {US_STATES.map((s) => (
              <option key={s.code} value={s.name} />
            ))}
          </datalist>
          <Button
            size="lg"
            onClick={onStateSubmit}
            className="text-lg px-8 py-6 h-auto rounded-2xl"
          >
            Continue
          </Button>
        </div>
      ) : null}

      {gate === "dob" ? (
        <div className="flex flex-col gap-3 text-left">
          <Label htmlFor="manual-dob">Date of birth</Label>
          <Input
            id="manual-dob"
            inputMode="numeric"
            autoComplete="bday"
            placeholder="MM/DD/YYYY"
            value={manualDobText}
            onFocus={onManualEntryStart}
            onChange={(e) => onDobText(e.target.value)}
          />
          <Button
            size="lg"
            onClick={onDobSubmit}
            className="text-lg px-8 py-6 h-auto rounded-2xl"
          >
            Continue
          </Button>
        </div>
      ) : null}

      {manualError ? (
        <p className="text-sm text-destructive text-left">{manualError}</p>
      ) : null}
    </div>
  );
};

const PhaseVisual = ({ phase }: { phase: Phase }) => {
  switch (phase) {
    case "playing_prompt":
      return (
        <>
          <SpeakerPulse />
          <p className="mt-6 text-2xl font-display text-foreground">Listen carefully</p>
          <p className="mt-2 text-base text-muted-foreground">
            We'll start recording when the question ends.
          </p>
        </>
      );
    case "recording":
      return (
        <>
          <RecordingPulse />
          <p className="mt-6 text-2xl font-display text-foreground">Listening</p>
          <p className="mt-2 text-base text-muted-foreground">
            Speak your answer. We'll stop when you finish.
          </p>
        </>
      );
    case "uploading":
      return (
        <>
          <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-muted">
            <Loader2 className="h-10 w-10 text-primary animate-spin" />
          </div>
          <p className="mt-6 text-2xl font-display text-foreground">Working on it</p>
          <p className="mt-2 text-base text-muted-foreground">One moment, please.</p>
        </>
      );
    case "awaiting_manual_confirm":
      // No spinner and no mention of what we thought we heard: the parsed value is
      // STT inference and surfacing it here would coach the answer on the one gate
      // where the typed value must stand on its own.
      return (
        <>
          <p className="mt-6 text-2xl font-display text-foreground">
            Please confirm your state
          </p>
          <p className="mt-2 text-base text-muted-foreground">
            We want to make sure we have this exactly right. Please enter the U.S.
            state you live in using the field below.
          </p>
        </>
      );
    default:
      return null;
  }
};

export default ConsentGate;
